import { useForm } from 'react-hook-form';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useLogin } from '../api/auth';
import { ApiError } from '../lib/errors';
import AuthShell from '../components/layout/AuthShell';
import Button from '../components/ui/Button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface FormValues { email: string; password: string; }

export default function LoginPage() {
  const { currentUser, login } = useAuth();
  const navigate = useNavigate();
  const { mutate, isPending } = useLogin();

  const { register, handleSubmit, setError, formState: { errors } } = useForm<FormValues>();

  if (currentUser) return <Navigate to="/dashboard" replace />;

  const onSubmit = (values: FormValues) => {
    mutate(values, {
      onSuccess: (dto) => { login(dto); navigate('/dashboard', { replace: true }); },
      onError: (err) => {
        const msg =
          err instanceof ApiError && err.code === 'ACCOUNT_LOCKED'
            ? 'Account is locked. Try again in 15 minutes or contact your administrator.'
            : 'Invalid email or password.';
        setError('root', { message: msg });
      },
    });
  };

  return (
    <AuthShell>
      <Card className="shadow-lg">
        <CardContent className="px-6 pb-6 pt-6">
          <div className="mb-6">
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-primary shadow-sm">
              <Zap className="h-5 w-5 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Sign in</h1>
            <p className="mt-1 text-sm text-muted-foreground">Welcome back to Cadence.</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {errors.root && (
              <div role="alert" className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {errors.root.message}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" autoFocus
                {...register('email', { required: 'Email is required' })} />
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-primary">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" autoComplete="current-password"
                {...register('password', { required: 'Password is required' })} />
              {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
            </div>

            <Button type="submit" className="w-full" loading={isPending}>
              {isPending ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            First time here?{' '}
            <Link to="/setup" className="font-medium text-primary hover:underline">Set up your account</Link>
          </p>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
